import { useState } from 'react';
import { Translation, useTranslation } from 'react-i18next';

import { Button, LoadingOverlay, ModalFooter } from '@any-ui-react/core';
import { useModals } from '@any-ui-react/modals';

interface SendMessageModalProps {
  isSchedule: boolean;
  onConfirm: () => Promise<boolean>;
  onClose: () => void;
}

const SendMessageModal = ({ isSchedule, onConfirm, onClose }: SendMessageModalProps) => {
  const { t } = useTranslation(['anychat', 'shared']);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (sending) return;

    setSending(true);
    const success = await onConfirm();
    setSending(false);
    if (success) {
      onClose();
    }
  };

  return (
    <>
      <LoadingOverlay visible={sending} overlayProps={{ blur: 2 }} zIndex={9999} />
      {t('anychat.broadcast.form.dialogs.send.desc', {
        isSchedule,
        ns: 'anychat',
      })}
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          {t('shared.button.cancel', { ns: 'shared' })}
        </Button>
        <Button onClick={handleSend}>
          {t('anychat.broadcast.form.dialogs.send.confirm', {
            isSchedule,
            ns: 'anychat',
          })}
        </Button>
      </ModalFooter>
    </>
  );
};

export type UseSendMessageModalProps = Pick<SendMessageModalProps, 'isSchedule'>;

const MODAL_ID = 'send-message-modal';

export const useSendMessageModal = ({ isSchedule }: UseSendMessageModalProps) => {
  const { openModal, closeModal } = useModals();

  const openSendMessageModal = async (onConfirm: SendMessageModalProps['onConfirm']) => {
    openModal({
      modalId: MODAL_ID,
      closeOnClickOutside: false,
      closeOnEscape: false,
      title: (
        <Translation ns="anychat">
          {(t) => <>{t('anychat.broadcast.form.dialogs.send.title', { isSchedule })}</>}
        </Translation>
      ),
      children: (
        <SendMessageModal
          isSchedule={isSchedule}
          onConfirm={onConfirm}
          onClose={() => closeModal(MODAL_ID)}
        />
      ),
    });
  };

  return { openSendMessageModal };
};
